// The hit flash on a struck body (decision 0047; suggestion "impact director"): a brightness per body that jumps to a
// peak on a hit and decays on real time (the brief: flashes decay on real time, so a hit-stop doesn't hold the
// screen white), drawn as an additive shell over the body's own meshes. The shell shares each mesh's geometry and is
// parented to it, so it follows any rig or joint that moves the mesh, and it is hidden at zero (no draw call).
import * as THREE from "three";

/** Planted defects beside what they break: the flash-cap check must go red with it on. */
export const FLASH_FAULTS = {
  /** The cap is ignored: every hit flashes, however many a second. */
  capIgnored: false,
};

/** Flash intensities by body id, decaying on real time. A cap (decision 0063: "a cap on how often the screen may
 *  flash") limits how many flashes may start in any one second of real time; a hit over the cap doesn't flash. */
export class Flashes {
  private v = new Map<number, { peak: number; life: number; age: number }>();
  /** Flashes started so far. */
  started = 0;
  /** Most flash starts in any one second of real time (Infinity: no cap). The player's accessibility setting. */
  cap = Infinity;
  /** Hits that would have flashed over the cap. */
  capped = 0;
  /** The most starts seen within one second (the check reads it against the cap). */
  peakRate = 0;
  private clock = 0;
  private starts: number[] = [];

  /** Start (or restart, keeping the brighter) a flash: `peak` (0..1) fading to 0 over `life` real seconds. Returns
   *  false when the cap held it back. */
  hit(id: number, peak: number, life: number): boolean {
    if (!(peak > 0) || !(life > 0)) return false;
    while (this.starts.length && this.starts[0]! <= this.clock - 1) this.starts.shift();
    if (this.starts.length >= this.cap && !FLASH_FAULTS.capIgnored) {
      this.capped++;
      return false;
    }
    const cur = this.value(id);
    this.v.set(id, { peak: Math.max(peak, cur), life, age: 0 });
    this.started++;
    this.starts.push(this.clock);
    this.peakRate = Math.max(this.peakRate, this.starts.length);
    return true;
  }

  /** Advance on real time. */
  update(realDt: number): void {
    this.clock += Math.max(0, realDt);
    for (const [id, f] of this.v) {
      f.age += Math.max(0, realDt);
      if (f.age >= f.life) this.v.delete(id);
    }
  }

  /** A body's flash now: peak × (1 − age/life)², 0 when none. */
  value(id: number): number {
    const f = this.v.get(id);
    if (!f) return 0;
    const k = 1 - f.age / f.life;
    return f.peak * k * k;
  }

  /** Bodies flashing now. */
  active(): number[] {
    return [...this.v.keys()];
  }
}

/** The additive material a shell draws with (one per shell; every shell shares one program). */
export function flashMaterial(colour: THREE.ColorRepresentation = "#ffffff"): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color: colour,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthFunc: THREE.LessEqualDepth,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    toneMapped: false,
  });
}

/** A flash shell over one body. */
export interface FlashShell {
  /** The shell's brightness (0 hides it). */
  set(intensity: number): void;
  /** Its brightness now. */
  readonly intensity: number;
  /** Remove the shell's meshes and its material. */
  dispose(): void;
}

/** Name the shell meshes carry (so a second shell, a debug view or a counter can skip them). */
export const FLASH_SHELL_NAME = "vfx:flash-shell";

/** Build a shell over every mesh under `root` (its own meshes only, not other shells). */
export function createFlashShell(root: THREE.Object3D, o: { colour?: THREE.ColorRepresentation } = {}): FlashShell {
  const material = flashMaterial(o.colour);
  const meshes: THREE.Mesh[] = [];
  const targets: THREE.Mesh[] = [];
  root.traverse((n) => {
    if ((n as THREE.Mesh).isMesh && n.name !== FLASH_SHELL_NAME) targets.push(n as THREE.Mesh);
  });
  for (const m of targets) {
    const s = new THREE.Mesh(m.geometry, material);
    s.name = FLASH_SHELL_NAME;
    s.renderOrder = (m.renderOrder ?? 0) + 1;
    s.visible = false;
    s.castShadow = false;
    s.receiveShadow = false;
    m.add(s);
    meshes.push(s);
  }
  let intensity = 0;
  return {
    set(v) {
      intensity = Math.max(0, Math.min(1, v));
      material.opacity = intensity;
      const on = intensity > 1e-3;
      for (const s of meshes) s.visible = on;
    },
    get intensity() {
      return intensity;
    },
    dispose() {
      for (const s of meshes) s.removeFromParent();
      material.dispose();
    },
  };
}
